import { useState, useEffect, useRef } from 'react'
import { Search as SearchIcon, ArrowRight } from 'lucide-react'
import { useApp } from '../../../context/App'
import { Section } from '../../../components/ui/Section'
import { api, urls } from '../../../services/api'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../../../components/ui/Modal'

export function Search() {
	const { configDomainURL } = useApp()
	const domain = configDomainURL
	const navigate = useNavigate()
	const containerRef = useRef<HTMLDivElement>(null)
	const [inputSearch, setInputSearch] = useState<string>('')
	const [suggestions, setSuggestions] = useState<any[]>([])
	const [showSuggestions, setShowSuggestions] = useState(false)
	const bgImageStyle = { backgroundImage: `url(${require('../../../assets/imgs/search.webp')})` }

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setShowSuggestions(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (inputSearch.length > 2) {
				handleAutocomplete(inputSearch)
			} else {
				setSuggestions([])
				setShowSuggestions(false) // Hide suggestions if input is too short
			}
		}, 300)

		return () => clearTimeout(delayDebounceFn)
	}, [inputSearch])

	const handleAutocomplete = async (term: string) => {
		try {
			const response = await api.get(urls.Pesquisa + term)
			setSuggestions(response.data.slice(0, 5)) // Limit to 5 suggestions
			setShowSuggestions(true)
		} catch (error) {
			// Error silenced for production cleanliness
			setSuggestions([])
			setShowSuggestions(false)
		}
	}

	const logSearch = (term: string) => {
		// Microsoft Clarity Custom Tag
		if ((window as any).clarity) {
			(window as any).clarity("set", "search_term", term);
		}
		// GTM DataLayer
		if ((window as any).dataLayer) {
			(window as any).dataLayer.push({
				event: 'search',
				search_term: term
			});
		}
	}

	const [quickService, setQuickService] = useState<{ label: string, url: string } | null>(null)

	const handleQuickService = (item: { label: string, url: string }) => {
		// All external services should open in a modal to confirm redirection
		setQuickService(item)
	}

	return (
		<Section aria-label="Busca principal">
			<div
				className="h-80 lg:h-80 flex flex-col gap-8 items-center justify-center bg-cover bg-center rounded-3xl relative overflow-hidden shadow-2xl group/box"
				style={bgImageStyle}
			>
				<div className="w-full h-full absolute top-0 left-0 bg-gradient-to-r from-primary/95 via-primary/60 to-primary/95 dark:from-surface-dark/95 dark:via-surface-dark/80 dark:to-surface-dark/95 backdrop-saturate-[1.2] transition-all duration-700 group-hover/box:opacity-80"></div>

				<div className="w-full h-full absolute flex flex-col items-center justify-center p-6 lg:p-12 z-10">
					<h2 className="text-white text-2xl lg:text-4xl font-bold font-roboto mb-6 lg:mb-10 text-center drop-shadow-md">
						Como podemos ajudar você hoje?
					</h2>

					<div className="relative w-full lg:w-2/3 max-w-2xl mb-6 lg:mb-10" ref={containerRef}>
						<form
							onSubmit={(e) => {
								e.preventDefault()
								if (inputSearch) {
									logSearch(inputSearch)
									navigate(`${domain}busca/${inputSearch}`)
								}
							}}
							className="w-full h-20 flex items-center group shadow-2xl rounded-2xl overflow-hidden ring-4 ring-white/20 focus-within:ring-secondary outline-none transition-all duration-300"
						>
							<input
								type='text'
								value={inputSearch}
								onChange={e => setInputSearch(e.target.value)}
								onFocus={() => inputSearch.length > 2 && setShowSuggestions(true)}
								placeholder='Procure por serviços, leis, notícias...'
								className="w-full h-full border-none pl-8 text-on-surface dark:text-on-surface-dark font-roboto font-normal text-lg bg-white/95 dark:bg-surface-dark-alt/95 backdrop-blur-md transition-all duration-300 group-hover:bg-white dark:group-hover:bg-surface-dark-alt outline-none placeholder:text-on-surface-subtle dark:placeholder:text-on-surface-dark-subtle rounded-l-2xl"
								aria-label="Campo de busca"
							/>
							<button
								type='submit'
								className="w-20 lg:w-28 h-full bg-secondary hover:bg-secondary-dark flex items-center justify-center transition-all duration-300 cursor-pointer border-none shadow-inner rounded-r-2xl"
								aria-label="Buscar"
							>
								<SearchIcon size={28} className="text-primary" />
							</button>
						</form>

						{/* Suggestions Dropdown */}
						<AnimatePresence>
							{showSuggestions && suggestions.length > 0 && (
								<motion.div
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -10 }}
									className="absolute top-[5.5rem] left-0 right-0 bg-white dark:bg-surface-dark-alt rounded-2xl shadow-premium overflow-hidden z-[50] border border-border-default dark:border-border-default-dark"
								>
									<div className="p-2 flex flex-col">
										{suggestions.map((item, idx) => (
											<button
												key={idx}
												className="flex items-center justify-between w-full p-4 hover:bg-primary/5 dark:hover:bg-white/5 rounded-xl transition-colors text-left border-none cursor-pointer group/item"
												onClick={() => {
													setInputSearch(item.title)
													setShowSuggestions(false)
													logSearch(item.title)
													navigate(`${domain}busca/${item.title}`)
												}}
											>
												<div className="flex flex-col">
													<span className="text-sm lg:text-base font-medium text-gray-900 dark:text-gray-100 line-clamp-1">{item.title}</span>
													<span className="text-[10px] lg:text-xs text-gray-400 uppercase tracking-widest mt-0.5">{item.category || 'Resultado'}</span>
												</div>
												<ArrowRight size={16} className="text-primary/30 group-hover/item:text-primary transition-all group-hover/item:translate-x-1" />
											</button>
										))}
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</div>

					<div className="flex flex-wrap items-center justify-center gap-3 lg:gap-6">
						{[
							{ label: "Aprova Digital", url: "https://itajai.aprova.com.br/" },
							{ label: "NFS-e", url: "https://servicos.itajai.sc.gov.br/servico/54-nfse.html" },
							{ label: "Jornais", url: "https://servicos.itajai.sc.gov.br/servico/76-jornal_do_municipio_de_itajai.html" },
							{ label: "Webmail", url: "https://webmail.itajai.sc.gov.br/" }
						].map((item, idx) => (
							<button
								key={idx}
								className="px-5 py-2 lg:px-8 lg:py-3 rounded-xl border border-white/20 text-white text-sm font-semibold bg-white/10 backdrop-blur-md transition-all duration-300 hover:bg-white/25 hover:border-white/40 hover:-translate-y-1 active:scale-95 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none cursor-pointer shadow-lg"
								onClick={() => handleQuickService(item)}
							>
								{item.label}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Quick Service Modal */}
			<Modal
				isOpen={!!quickService}
				onClose={() => setQuickService(null)}
				title={quickService?.label || 'Serviço'}
			>
				<div className="flex flex-col gap-6">
					<p className="text-gray-600 dark:text-gray-400 leading-relaxed text-base">
						Você está prestes a acessar o serviço de <strong>{quickService?.label}</strong>.
						Deseja abrir o portal oficial em uma nova aba?
					</p>
					<div className="flex flex-col sm:flex-row gap-4">
						<button
							onClick={() => {
								window.open(quickService?.url, "_blank")
								setQuickService(null)
							}}
							className="flex-1 py-4 bg-primary dark:bg-primary-light text-white font-bold rounded-2xl hover:brightness-110 active:scale-95 transition-all border-none cursor-pointer"
						>
							Acessar Portal
						</button>
						<button
							onClick={() => setQuickService(null)}
							className="flex-1 py-4 bg-surface-alt dark:bg-surface-dark-alt text-on-surface dark:text-on-surface-dark font-bold rounded-2xl hover:bg-gray-200 dark:hover:bg-white/10 active:scale-95 transition-all border-none cursor-pointer"
						>
							Cancelar
						</button>
					</div>
				</div>
			</Modal>
		</Section>
	)
}